// If you want to use Phoenix channels, run `mix help phx.gen.channel`
// to get started and then uncomment the line below.
// import "./user_socket.js"

// You can include dependencies in two ways.
//
// The simplest option is to put them in assets/vendor and
// import them using relative paths:
//
//     import "../vendor/some-package.js"
//
// Alternatively, you can `npm install some-package --prefix assets` and import
// them using a path starting with the package name:
//
//     import "some-package"
//

// Include phoenix_html to handle method=PUT/DELETE in forms and buttons.
import 'phoenix_html'
import 'ninja-keys'
import posthog from 'posthog-js'
import reconnectAfterMs from './utils/reconnect-after-ms'

posthog.init('phc_nZZ9aj2xwkzCG4F3IZ6mQ3eOT6zOQu9Ca8VQ6NMED2A', { api_host: 'https://us.i.posthog.com' })

// Establish Phoenix Socket and LiveView configuration.
import { Socket } from 'phoenix'
import { LiveSocket } from 'phoenix_live_view'

// import initFrontend from './frontend/init'
import { appsignal } from './appsignal'
import PetalFrameworkHooks from '../../deps/petal_framework/assets/js/hooks'
import LiveNotetakerHook from './liveNotetaker/hook'
import { WebRecorder2Hook, WebRecorder2MeetingDetailHook } from './webRecorder2/hook'
import { ConfirmDeleteMeetingHook } from './hooks/ConfirmDeleteMeeting'
import PlaybackSyncHook from './hooks/playback-sync'
import Sortable from './hooks/sortable'
import IndeterminateCheckbox from './hooks/IndeterminateCheckbox'
import CursorOnFocus from './hooks/cursor-on-focus'
import DatePickerHook from './hooks/date-picker'
import DateTimePickerHook from './hooks/datetime-picker'
import ProxyHook from './hooks/proxy'
import ContentEditableInputHook from './hooks/contenteditable-input'
import MagicTyper from './hooks/magic-typer'
import AutoTitle from './hooks/auto-title.js'
import DropZone from './hooks/drop-zone'
import FocusFirst from './hooks/focus-first'
import CopyHtml from './hooks/copy-html'
import RichTextCopy from './hooks/rich-text-copy'
import CurrencyFormatter from './hooks/currency-formatter'
import GoogleTagManager from './hooks/google-tag-manager'
import SelectTextTooltip from './hooks/select-text-tooltip'
import AutoPlayAtTimestamp from './hooks/auto-play-at-timestamp'
import PreventTextSelection from './hooks/prevent-text-selection'
import ClearFlash from './hooks/clear-flash'
import Phone from './hooks/phone'
import ToastsHook from './toasts/hook'
import startUrlWatcher from './utils/url-watcher.js'
import PhoenixCustomEvent from 'phoenix-custom-event-hook'
import S3 from './uploaders/s3'
import RestrictInput from './hooks/restrict-input'
import truncateChar from './utils/truncate-char'
import DisableOnClick from './hooks/disable-on-click'
import AvatarLoader from './hooks/avatar-loader'
import AutoGrowTextarea from './hooks/auto-grow-textarea'
import ComboboxRefocusHook from './hooks/combobox-refocus.js'
import MermaidHook from './hooks/mermaid.js'
import MermaidPanZoom from './hooks/mermaid_pan_zoom'
import ReCaptcha from './hooks/recaptcha'
import JumpComboBoxHook from './hooks/combo_box'
import AISourcePopover from './hooks/ai-source-popover'
import GraphViz from './hooks/graphviz'
import EnterKeySubmitModal from './hooks/enter-key-submit-modal'
import ExportOnce from './hooks/export_once_hook.js'
import ClickOutside from './hooks/click-outside'
// charts
import ChartHooks from './hooks/charts'

import { clearCompletedRecordingStorage } from './webRecorder2/recordingStorage'
import * as GlobalPubSub from './pubSub'

// custom web components
import './web-components/rich-text-editor-with-search'
import './web-components/avatar'
import EnterKeySubmit from './hooks/enter-key-submit'
import ScrollToBottom from './hooks/scroll-to-bottom.js'
import InfiniteScroll from './hooks/infinite-scroll.js'
import { customErrorHandler } from './utils/console'

async function initialize() {
  let csrfToken = document.querySelector("meta[name='csrf-token']").getAttribute('content')
  let url = '/live'
  if (window.location.pathname.startsWith('/admin')) {
    url = '/admin/live'
  }

  let zoomSdk = window.zoomSdk || null

  let params = {
    _csrf_token: csrfToken,
    locale: Intl.NumberFormat().resolvedOptions().locale,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timezone_offset: -(new Date().getTimezoneOffset() / 60),
    user_agent: navigator.userAgent,
  }

  if (zoomSdk) {
    const configResponse = await zoomSdk.config({
      version: '0.16',
      popoutSize: { width: 480, height: 360 },
      capabilities: ['getUserContext'],
    })

    const user = await zoomSdk.getUserContext()

    const zoom_in_client_app = {
      user: user,
      config_response: configResponse,
    }

    params = { ...params, zoom_in_client_app }
  }

  let liveSocket = new LiveSocket(url, Socket, {
    hooks: {
      DateTimePickerHook,
      DatePickerHook,
      LiveNotetakerHook,
      WebRecorder2Hook,
      WebRecorder2MeetingDetailHook,
      ConfirmDeleteMeetingHook,
      PlaybackSyncHook,
      Sortable,
      IndeterminateCheckbox,
      CursorOnFocus,
      ProxyHook,
      ContentEditableInputHook,
      MagicTyper,
      AutoTitle,
      DropZone,
      FocusFirst,
      CopyHtml,
      RichTextCopy,
      CurrencyFormatter,
      GoogleTagManager,
      PhoenixCustomEvent,
      SelectTextTooltip,
      AISourcePopover,
      AutoPlayAtTimestamp,
      EnterKeySubmit,
      EnterKeySubmitModal,
      ScrollToBottom,
      InfiniteScroll,
      Phone,
      ClearFlash,
      ToastsHook,
      PreventTextSelection,
      RestrictInput,
      DisableOnClick,
      AvatarLoader,
      AutoGrowTextarea,
      ComboboxRefocusHook,
      MermaidHook,
      MermaidPanZoom,
      ReCaptcha,
      JumpComboBoxHook,
      ExportOnce,
      GraphViz,
      ClickOutside,
      ...ChartHooks,
      ...PetalFrameworkHooks,
    },
    reconnectAfterMs,
    uploaders: { S3: S3 },
    dom: {
      onBeforeElUpdated(from, to) {
        if (from._x_dataStack) {
          window.Alpine.clone(from, to)
        }
      },
    },
    viewLogger: (view, kind, msg, obj) => {
      if (['join', 'destroyed', 'error', 'timeout', 'hook'].includes(kind)) {
        console.log(`[LiveView ${kind}] ${msg}\nAffected object:`, obj, '\nView:', view)
      }
    },
    params,
  })

  liveSocket.socket.onClose(event => {
    if (!event.wasClean) {
      appsignal.sendError(new Error('WebSocket disconnected dirtily'), span => {
        span.setAction('WebSocket Disconnected')
        span.setTags({
          reason: event.reason,
          code: event.code,
        })
      })
    }
  })

  liveSocket.socket.onError(error => {
    appsignal.sendError(new Error('WebSocket error occurred'), span => {
      span.setAction('WebSocket Error')
      span.setTags({
        message: error.message,
      })
    })
  })

  // Show progress bar on live navigation and form submits
  // topbar.config({ barColors: { 0: '#60781B' }, shadowColor: 'rgba(0, 0, 0, .3)' })
  // window.addEventListener('phx:page-loading-start', (info) => topbar.delayedShow(200))
  // window.addEventListener('phx:page-loading-stop', (info) => topbar.hide())

  // connect if there are any LiveViews on the page
  liveSocket.connect()

  // expose liveSocket on window for web console debug logs and latency simulation:
  // >> liveSocket.enableDebug()
  // >> liveSocket.enableLatencySim(1000)  // enabled for duration of browser session
  // >> liveSocket.disableLatencySim()
  window.liveSocket = liveSocket

  window.simulateDeploy = function() {
    window.liveSocket.disconnect(() => {
      window.liveSocket.connect()
    })
  }

  window.addEventListener('phx:copy', (event) => {
    navigator.clipboard.writeText(event.detail.text).then(() => {
      console.log('Copied')
    })
  })

  window.addEventListener('phx:clear_saved_input', (event) => {
    Object.keys(localStorage).forEach((key) => {
      if (key.includes('saved-value')) {
        localStorage.removeItem(key)
      }
    })
  })

  window.addEventListener('phx:rich_copy', (event) => {
    const htmlContent = event.detail.text
    const blob = new Blob([htmlContent], { type: 'text/html' })
    const clipboardItem = new ClipboardItem({ 'text/html': blob })
    navigator.clipboard
      .write([clipboardItem])
      .then(() => {
        console.log('Rich text copied successfully!')
        // You can add a nice tooltip or other UI feedback here
      })
      .catch((error) => {
        console.error('Failed to copy rich text:', error)
      })
  })

  window.addEventListener('phx:download-csv', (event) => {
    downloadFile(event, 'text/csv')
  })


  window.addEventListener('phx:open_blank', (event) => {
    window.open(event.detail.url, '_blank')
  })

  window.addEventListener("phx:download-file", (event) => {
    const { filetype } = event.detail
    console.log("filetype")
    console.log(filetype)
    downloadFile(event, filetype)
  });

  function downloadFile(event, type) {
    const { content, filename } = event.detail
    const blob = new Blob([content], { type: type })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  }

  const sharedTomSelectOptions = {
    no_results: function(data) {
      return (
        '<div class="no-results">No results found for <span class="font-medium">' +
        escapeOutput(data.input) +
        '</span></div>'
      )
    },
    loading: function(data) {
      return '<div class="flex items-center justify-center px-5 py-5"><img class="w-6 h-6" src="/images/icon-jump.svg" /></div>'
    },
  }

  window.multipleSelectClearSearchText = {
    onItemAdd: function() {
      this.setTextboxValue('')
      this.refreshOptions()
    },
  }

  window.basicTomSelectOptions = {
    preload: true,
    render: {
      ...sharedTomSelectOptions,
    },
  }

  window.subtitleComboBoxOptions = {
    maxOptions: null,
    render: {
      option: function(data) {
        const json = JSON.parse(data.text)

        let detail = ''
        if (json.detail) {
          detail = `<div style="color: gray; font-size: 12px;">${escapeOutput(json.detail) || ''}</div>`
        }

        return `
        <div>
          <div>${escapeOutput(json.title)}</div>
          <div style="color: gray; font-size: 12px;">${escapeOutput(json.subtitle) || ''}</div>
          ${detail}
        </div>
        `
      },
      item: function(data) {
        const json = JSON.parse(data.text)
        if (!json.subtitle) return `<div>${json.title}</div>`
        return `<div>${json.title} | ${json.subtitle}</div>`
      },
      ...sharedTomSelectOptions,
    },
  }

  window.linkParticipantToContactComboBoxOptions = {
    openOnFocus: false,
    onLoad: function() {
      this.focus();
    },
    maxOptions: null,
    // Head start on v2 when we need creation
    // create: function(_) {
    //   const linkContactSearchSelect = document.getElementById('link-contact-search_select');
    //   if (linkContactSearchSelect) {
    //     const participantName = linkContactSearchSelect.getAttribute('phx-value-participant-name');
    //     const participantEmail = linkContactSearchSelect.getAttribute('phx-value-participant-email');
    //   return {
    //     value: "create_contact",
    //     text: JSON.stringify({
    //       title: participantName,
    //       subtitle: participantEmail,
    //     }),
    //   };
    // }
    // },
    render: {
      option: function(data) {
        const json = JSON.parse(data.text)
        const serviceLogosHtml = json.services
          ? json.services
            .map(
              ({ isConnected, service }) => {
                const className = isConnected ? "" : "grayscale opacity-30"
                return `<img class="h-5 shrink-0 ${className}" src="/images/logos/logo-mini-${service.toLowerCase()}.svg" />`
              }
            )
            .join('')
          : ''

        let detail = ''
        if (json.detail) {
          detail = `<div style="color: gray; font-size: 12px;">${escapeOutput(json.detail) || ''}</div>`
        }

        return `
          <div class="flex items-center gap-2 p-2">
            <jump-avatar
              name="${escapeOutput(json.title)}"
              size="tiny"
              randomColor="true"
            ></jump-avatar>
            <div class="flex-1">
              <div class="font-medium">${escapeOutput(json.title)}</div>
              <div class="text-secondary-text text-sm">${escapeOutput(json.subtitle) || ''}</div>
              ${detail}
            </div>
            <div class="flex gap-2">
              ${serviceLogosHtml}
            </div>
          </div>
        `
      },
      item: function(data) {
        const json = JSON.parse(data.text)
        return `
        <div class="flex items-center gap-2">
          <jump-avatar
            name="${escapeOutput(json.title)}"
            size="xs"
            randomColor="true"
          ></jump-avatar>
          <div class="font-medium">${escapeOutput(json.title)}</div>
        </div>
      `
      },
      option_create: function(data) {
        const linkContactSearchSelect = document.getElementById('link-contact-search_select')
        if (linkContactSearchSelect) {
          const participantName = linkContactSearchSelect.getAttribute('phx-value-participant-name')
          const participantEmail = linkContactSearchSelect.getAttribute('phx-value-participant-email')
          return `
        <div class="create flex flex-col">
          <div class="h-9 px-2 pt-4 pb-1 rounded-md justify-start items-center gap-1.5 inline-flex">
            <div class="grow shrink basis-0 h-4 justify-start items-center gap-1 flex">
              <div class="grow shrink basis-0 pl-1 pr-7 flex-col justify-start items-start inline-flex">
                <div class="self-stretch justify-start items-start gap-2 inline-flex">
                  <div class="grow shrink basis-0 text-secondary-text text-[13px] font-medium leading-none">Create new contact</div>
                </div>
              </div>
            </div>
          </div>
          <div class="flex items-center gap-2 pr-2 pb-2">
            <jump-avatar
              name="${escapeOutput(participantName)}"
              size="tiny"
              randomColor="true"
            ></jump-avatar>
            <div class="flex-1">
              <div class="font-medium">${escapeOutput(participantName)}</div>
              <div class="text-secondary-text text-sm">${escapeOutput(participantEmail) || ''}</div>
            </div>
            <div class="relative">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 5C10 6.65685 8.65685 8 7 8C5.34315 8 4 6.65685 4 5C4 3.34315 5.34315 2 7 2C8.65685 2 10 3.34315 10 5Z" fill="#A7B0BA"/>
                <path d="M1.61528 16.428C1.21798 16.1736 0.987847 15.721 1.04605 15.2529C1.41416 12.292 3.93944 10 6.9999 10C10.0604 10 12.5856 12.2914 12.9537 15.2522C13.012 15.7203 12.7818 16.1729 12.3845 16.4273C10.8302 17.4225 8.98243 18 6.9999 18C5.01737 18 3.16959 17.4231 1.61528 16.428Z" fill="#A7B0BA"/>
                <path d="M16.25 5.75C16.25 5.33579 15.9142 5 15.5 5C15.0858 5 14.75 5.33579 14.75 5.75V7.75H12.75C12.3358 7.75 12 8.08579 12 8.5C12 8.91421 12.3358 9.25 12.75 9.25H14.75V11.25C14.75 11.6642 15.0858 12 15.5 12C15.9142 12 16.25 11.6642 16.25 11.25V9.25H18.25C18.6642 9.25 19 8.91421 19 8.5C19 8.08579 18.6642 7.75 18.25 7.75H16.25V5.75Z" fill="#A7B0BA"/>
              </svg>
            </div>
          </div>
        </div>
        `
        }
        return sharedTomSelectOptions.option_create(data)
      },
      ...sharedTomSelectOptions,
    },
  }

  window.intRefComboBoxOptions = {
    maxOptions: null,
    preload: true,
    loadThrottle: 700,
    onInitialize: function() {
      const proxy = ProxyHook.findProxyParent(this.input)
      if (proxy) {
        this.open()
        this.on('blur', () => {
          if (this.input.dataset.blur) {
            proxy.proxyPushEventTo(this.input.dataset.blur, { value: this.getValue() })
          }
        })
      }
    },
    onChange: function(value, data) {
      this.blur()
    },
    render: {
      option: function(data) {
        const json = JSON.parse(data.text)

        return `
        <div style="display: flex; align-items: center; justify-content: space-between; padding: 8px;">
          <div class="flex-1 ml-2">
            <span>${escapeOutput(json.title)}</span>
            <div style="color: gray; font-size: 14px; margin-top: 2px;">${escapeOutput(json.subtitle) || ''}</div>
          </div>
          <span style="padding: 6px 12px; background-color: #DFE4E7; border-radius: 20px; font-size: 12px; font-weight: 600;">${json.type}</span>
        </div>
        `
      },
      item: function(data) {
        const json = JSON.parse(data.text)
        return `<div>${escapeOutput(json.title)} | ${escapeOutput(json.subtitle) || ''}</div>`
      },
      ...sharedTomSelectOptions,
    },
  }

  window.generateMeetingPrepIntRefComboBoxOptions = {
    ...intRefComboBoxOptions,
    onChange: function(value, data) {
      const form = this.input.closest('form')
      const button = form.querySelector('button[type="submit"]')
      const disabled = value.length == 0
      button.disabled = disabled
      if (disabled) {
        button.classList.add('pc-button--disabled')
      } else {
        button.classList.remove('pc-button--disabled')
      }
    },
    onItemAdd: function() {
      this.setTextboxValue('')
      this.refreshOptions()
    },
    render: {
      option: function(data) {
        const json = JSON.parse(data.text)

        return `
        <div style="display: flex; align-items: start; justify-content: space-between; padding: 8px;">
          <div style="flex-grow: 3">
            <span>${escapeOutput(json.title)}</span>
            <div style="color: gray; font-size: 14px; margin-top: 2px;">${escapeOutput(json.subtitle) || ''}</div>
          </div>
          <span style="padding: 6px 12px; background-color: #DFE4E7; border-radius: 20px; font-size: 12px; font-weight: 600;">${json.type}</span>
        </div>
        `
      },
      item: function(data) {
        const json = JSON.parse(data.text)
        return `<div>${escapeOutput(json.title)} | ${escapeOutput(json.subtitle) || ''}</div>`
      },
      ...sharedTomSelectOptions,
    },
  }

  const taskStatusOption = (json) => {
    const comboBoxElement = document.querySelector('#truncate-selected')
    const parentWidth = comboBoxElement.parentElement.offsetWidth
    const maxLength = parentWidth / 14

    return `
        <div class="flex items-center font-medium gap-x-1">
          <div class="w-3 h-3 rounded-full" style="background-color: ${json.color};"></div>
          <div>${truncateChar(escapeOutput(json.name), maxLength)}</div>
        </div>
        `
  }

  window.taskStatusComboBoxOptions = {
    maxOptions: null,
    render: {
      option: function(data) {
        const json = JSON.parse(data.text)
        return taskStatusOption(json)
      },
      item: function(data) {
        const json = JSON.parse(data.text)
        return taskStatusOption(json)
      },
      ...sharedTomSelectOptions,
    },
  }

  const userOption = (json) => {
    return `
        <div class="flex">
          <div class="flex items-center whitespace-nowrap rounded-full font-medium gap-x-1 bg-tertiary h-7 pl-1 pr-3 text-sm">
            <div class="w-5 h-5 rounded-full text-primary-inverse text-[10px] flex items-center justify-center" style="background-color: ${json.color};">
              ${json.initials}
            </div>
            <div>${escapeOutput(json.name)}</div>
          </div>
        </div>
        `
  }
  window.userComboBoxOptions = {
    maxOptions: null,
    render: {
      option: function(data) {
        const json = JSON.parse(data.text)
        return userOption(json)
      },
      item: function(data) {
        const json = JSON.parse(data.text)
        return userOption(json)
      },
      ...sharedTomSelectOptions,
    },
  }

  const assignableOption = (json) => {
    let sources = json.sources.map((source) => `<img src="/images/logo-source-${source}.svg" />`).join(' ')

    return `
        <div class="flex items-center justify-between">
          <div class="flex items-center whitespace-nowrap gap-x-2 h-7 pl-1 pr-3">
            <div class="w-5 h-5 rounded-full text-primary-inverse text-[10px] flex items-center justify-center" style="background-color: ${json.color};">
              ${json.initials}
            </div>
            <div>${escapeOutput(json.title)}</div>
          </div>
          <div class="flex items-center gap-x-2">
            ${sources}
          </div>
        </div>
        `
  }

  window.taskUserComboBoxOptions = {
    maxOptions: null,
    // preload: true,
    render: {
      option: function(data) {
        const json = JSON.parse(data.text)
        return assignableOption(json)
      },
      item: function(data) {
        const json = JSON.parse(data.text)
        return assignableOption(json)
      },
      ...sharedTomSelectOptions,
    },
    onInitialize: function() {
      const proxy = ProxyHook.findProxyParent(this.input)
      if (this.input.dataset.autoOpen) {
        this.open()
      }
      this.on('blur', () => {
        if (this.input.dataset.change) {
          proxy.proxyPushEventTo(this.input.dataset.change, { value: this.getValue() })
        }
      })
    },
    onChange: function() {
      const proxy = ProxyHook.findProxyParent(this.input)
      if (this.getValue() && this.input.dataset.change) {
        proxy.proxyPushEventTo(this.input.dataset.change, { value: this.getValue() })
      }
    },
  }

  window.schemaComboBoxOptions = {
    maxOptions: null,
    render: {
      ...sharedTomSelectOptions,
    },
    onInitialize: function() {
      const proxy = ProxyHook.findProxyParent(this.input)
      this.open()
      /**
       * HACK: 🤡 @blakedietz 2025-02-20 - in certain situations when the combobox is inside
       * of a container that can scroll horizontally, if the combobox is clicked the scroll
       * position is reset. Unfortunately I've spent enough time trying to find the root cause
       * to no avail. So instead of a true fix, I offer you this ugly bandaid 🩹.
       * Assume that places where `data-scroll-into-view="true"` is set are parts of the product
       * that have been reported to be impacted by the bug. These usages are by no means
       * exhaustive and there may be more.
       */
      this.on('focus', () => {
        if (this.input.dataset.scrollIntoView == 'true') {
          this.input.scrollIntoView({ inline: 'start' })
        }
      })
      this.on('blur', () => {
        if (this.input.dataset.blur) {
          proxy.proxyPushEventTo(this.input.dataset.blur, { value: this.getValue() })
        }
      })
    },
  }

  const sourceOption = (data) => {
    const json = JSON.parse(data.text)
    let sources = json.sources.map((source) => `<img src="/images/logo-source-${source}.svg" />`)

    return `
        <div class="flex items-center">
          <div class="flex items-center justify-between w-full">
            <div class="flex items-center whitespace-nowrap gap-x-2 h-7 pl-1 pr-3">
              ${escapeOutput(json.title)}
            </div>
            <div class="flex items-center gap-x-1">
              ${sources}
            </div>
          </div>
        </div>
        `
  }

  window.sourceComboBoxOptions = {
    maxOptions: null,
    render: {
      option: sourceOption,
      item: sourceOption,
      ...sharedTomSelectOptions,
    },
  }

  window.schemaTypeComboBoxOptions = {
    maxOptions: 500,
    render: {
      option: function(data) {
        const json = JSON.parse(data.text)

        return `
        <div class="flex justify-between">
          <div>${escapeOutput(json.title)}</div>
          <div style="color: gray; font-size: 12px;">${escapeOutput(json.subtitle) || ''}</div>
        </div>
        `
      },
      item: function(data) {
        const json = JSON.parse(data.text)
        if (json.title !== json.subtitle) {
          return `<div>${escapeOutput(json.title)} | ${escapeOutput(json.subtitle)}</div>`
        } else {
          return `<div>${escapeOutput(json.title)}</div>`
        }
      },
      ...sharedTomSelectOptions,
    },
  }

  window.checkboxTomSelectOptions = {
    render: {
      option: function(data, escape) {
        let tempDiv = document.createElement('div')
        tempDiv.innerHTML = this.wrapper.innerHTML

        let selectedValue = tempDiv.querySelector('.ts-control div.item')?.getAttribute('data-value')
        let isChecked = selectedValue === data.value ? 'checked' : ''

        const whiteTickPath = '/images/tick.svg'

        // If you use the `tom_select_options` to set the options, as we do in
        // `JumpWeb.Components.Suggestion`, `data` will be a JavaScript object, and you must
        // provide the `title` and `value` at minimum (optionally `disabled` and `detail`).
        // On the other hand, if you set the options via the `options` attribute on the
        // `<.combo_box>`, as in `JumpWeb.Components.MicrosoftTeamsSettings`, `data.text` will
        // be the name of the option.
        const json =
          typeof data === 'object' && data && data.title
            ? data
            : data.text.startsWith('{')
              ? JSON.parse(data.text)
              : { title: data.text }

        const isDisabled = !!json.disabled

        return `
          <div
            id=${this.input.id}
            class="flex items-center justify-between gap-2 px-4 py-2 rounded-sm hover:bg-sky-50 ${isChecked ? 'bg-sky-50' : ''} ${isDisabled ? '!opacity-50 pointer-events-none' : ''}"
            ${isDisabled ? 'disabled="true"' : ''}
          >
            <div class="flex flex-col">
              <div class="text-primary text-base">${escapeOutput(json.title)}</div>
              <div class="text-secondary text-sm">${escapeOutput(json.detail || '')}</div>
            </div>
            <div class="checkmark w-5 h-5 flex shrink-0 items-center justify-center border rounded-full ${isChecked ? 'bg-black' : 'border-gray-300'}">
              ${isChecked ? `<img src="${whiteTickPath}" class="w-3 h-3" />` : ''}
            </div>
          </div>
        `
      },
      item: function(data, escape) {
        const json =
          typeof data === 'object' && data && data.title
            ? data
            : data.text.startsWith('{')
              ? JSON.parse(data.text)
              : { title: data.text }
        return `<div>${escapeOutput(json.title)}</div>`
      },
      ...sharedTomSelectOptions,
    },
    onChange: function(selectedValue) {
      let dropdown = document.getElementById(this.input.id + '-ts-dropdown')

      dropdown.querySelectorAll(':scope > div').forEach((option) => {
        let value = option.getAttribute('data-value')
        let checkmark = option.querySelector('.checkmark')

        const whiteTickPath = '/images/tick.svg'

        if (value === selectedValue) {
          option.classList.add('bg-sky-50')
          if (checkmark) {
            checkmark.innerHTML = `<img src="${whiteTickPath}" class="w-3 h-3" />`
            checkmark.classList.add('bg-black')
          }
        } else {
          option.classList.remove('bg-sky-50')
          if (checkmark) {
            checkmark.innerHTML = ''
            checkmark.classList.remove('bg-black')
          }
        }
      })
    },
  }

  window.globalSearchv2TomSelectOptions = {
    maxOptions: null,
    openOnFocus: true,
    preload: 'focus',
    dropdownParent: 'body',
    optgroupField: 'type',
    optgroupLabelField: 'label',
    lockOptgroupOrder: true,
    optgroups: [
      { value: 'contact', label: 'Contacts', order: 1 },
      { value: 'meeting', label: 'Meetings', order: 2 }
    ],
    shouldLoad: () => true,
    render: {
      dropdown: function() {
        return `
          <div class="ts-dropdown flex flex-col">
            <div class="self-stretch w-full px-2 py-2.5 border-b border-[#dfe4e7] inline-flex justify-start items-start gap-2.5">
              <div class="flex-1 h-9 max-w-[720px] min-w-80 inline-flex flex-col justify-start items-start overflow-hidden">
                <div class="self-stretch inline-flex justify-start items-start gap-2">
                  <div class="flex-1 pl-3 pr-4 py-2 bg-white rounded-md flex justify-start items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 text-[#69717a]">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                    </svg>
                    <div class="ts-control-placeholder w-full"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        `;
      },
      optgroup_header: function(data, escape) {
        return `<div data-multi-select="False" data-selected="False" data-settings-List Item="Default" data-state="Default" class="self-stretch px-5 pt-4 pb-1 rounded-md inline-flex justify-start items-center gap-1.5">
        <div class="flex-1 flex justify-start items-center gap-1">
          <div class="self-stretch pl-1 pr-7 inline-flex flex-col justify-start items-start">
            <div class="self-stretch inline-flex justify-start items-start gap-2">
              <div>${escape(data.label)}</div>
            </div>
          </div>
        </div>
      </div>`
      },
      option: function(data) {
        const json = data.json || JSON.parse(data.text);
        if (data.type === 'contact') {
          return `
            <div class="flex items-center justify-between px-2">
              <div class="flex items-center gap-2">
                <jump-avatar
                  name="${escapeOutput(json.name)}"
                  size="tiny"
                  randomColor="true"
                ></jump-avatar>
                <div class="font-medium">${escapeOutput(json.name)}</div>
              </div>
              <div class="text-secondary text-sm">${escapeOutput(json.email)}</div>
            </div>
          `;
        } else {
          return `
            <div class="flex items-center justify-between px-2">
              <div class="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5m-9-6h.008v.008H12v-.008ZM12 15h.008v.008H12V15Zm0 2.25h.008v.008H12v-.008ZM9.75 15h.008v.008H9.75V15Zm0 2.25h.008v.008H9.75v-.008ZM7.5 15h.008v.008H7.5V15Zm0 2.25h.008v.008H7.5v-.008Zm6.75-4.5h.008v.008h-.008v-.008Zm0 2.25h.008v.008h-.008V15Zm0 2.25h.008v.008h-.008v-.008Zm2.25-4.5h.008v.008H16.5v-.008Zm0 2.25h.008v.008H16.5V15Z" />
                </svg>
                <div class="font-medium">${escapeOutput(json.name)}</div>
              </div>
              <div class="text-secondary text-sm">${escapeOutput(json.date)}</div>
            </div>
          `;
        }
      },
      loading: sharedTomSelectOptions.loading,
      no_results: function(data) {
        if (!data.input || data.input === "") {
          return `
            <div class="self-stretch px-2 pt-1 pb-3 flex flex-col justify-start items-start overflow-hidden">
              <div class="self-stretch px-2 pt-4 pb-1 rounded-md inline-flex justify-start items-center gap-1.5">
                <div class="flex-1 flex justify-start items-center gap-1">
                  <div class="self-stretch pl-1 pr-7 inline-flex flex-col justify-start items-start">
                    <div class="self-stretch inline-flex justify-start items-start gap-2">
                      <div class="text-secondary text-sm">Search across:</div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="self-stretch p-2 rounded-md inline-flex justify-start items-center gap-1">
                <div class="self-stretch inline-flex justify-start items-center gap-1">
                  <div class="inline-flex flex-col justify-start items-start overflow-hidden">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 mt-0.5">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M17.982 18.725A7.488 7.488 0 0 0 12 15.75a7.488 7.488 0 0 0-5.982 2.975m11.963 0a9 9 0 1 0-11.963 0m11.963 0A8.966 8.966 0 0 1 12 21a8.966 8.966 0 0 1-5.982-2.275M15 9.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                    </svg>
                  </div>
                  <div class="self-stretch pl-1 inline-flex flex-col justify-start items-start">
                    <div class="self-stretch inline-flex justify-start items-start gap-2">
                      <div class="font-medium">Contacts</div>
                    </div>
                    <div class="text-secondary text-sm">Search by name or email</div>
                  </div>
                </div>
              </div>
              <div class="self-stretch p-2 rounded-md inline-flex justify-start items-center gap-1">
                <div class="self-stretch inline-flex justify-start items-center gap-1">
                  <div class="inline-flex flex-col justify-start items-start">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 mt-0.5">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5m-9-6h.008v.008H12v-.008ZM12 15h.008v.008H12V15Zm0 2.25h.008v.008H12v-.008ZM9.75 15h.008v.008H9.75V15Zm0 2.25h.008v.008H9.75v-.008ZM7.5 15h.008v.008H7.5V15Zm0 2.25h.008v.008H7.5v-.008Zm6.75-4.5h.008v.008h-.008v-.008Zm0 2.25h.008v.008h-.008V15Zm0 2.25h.008v.008h-.008v-.008Zm2.25-4.5h.008v.008H16.5v-.008Zm0 2.25h.008v.008H16.5V15Z" />
                    </svg>
                  </div>
                  <div class="self-stretch pl-1 inline-flex flex-col justify-start items-start">
                    <div class="self-stretch inline-flex justify-start items-start gap-2">
                      <div class="font-medium">Meetings</div>
                    </div>
                    <div class="text-secondary text-sm">Search by title or date</div>
                  </div>
                </div>
              </div>
            </div>
          `;
        }
        // If there are no results for a search, show the standard "no results" message
        return `
          <div class="px-4 py-3">
            <div class="text-secondary">No results found for <span class="font-medium text-black">${escapeOutput(data.input)}</span></div>
          </div>
        `;
      },
    },
    onInitialize: function() {
      this.formatter = new Intl.DateTimeFormat(navigator.language, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
      const dropdown = this.dropdown;
      const instance = this;
      dropdown.classList.add('global-search-v2');

      // Create a dummy input in place of the original
      const originalInput = instance.control;
      const dummyInput = document.createElement('input');
      dummyInput.type = 'text';
      dummyInput.className = originalInput.className;
      dummyInput.placeholder = originalInput.querySelector('input').placeholder;
      dummyInput.readOnly = true;

      // Replace original with dummy
      originalInput.parentNode.insertBefore(dummyInput, originalInput);

      // Move original control to modal
      const controlPlaceholder = dropdown.querySelector('.ts-control-placeholder');
      controlPlaceholder.appendChild(originalInput);

      // Handle dummy input click to open dropdown
      dummyInput.addEventListener('click', () => {
        instance.open();
      });

      // Update dummy input when modal input changes
      instance.on('type', (str) => {
        dummyInput.value = str;
      });

      instance.on('item_add', (value, item) => {
        dummyInput.value = item.textContent;
      });

      instance.on('clear', () => {
        dummyInput.value = '';
      });

      // Handle backdrop and focus effects
      this.on('dropdown_open', function() {
        document.body.classList.add('ts-backdrop-active');
        document.body.style.overflow = 'hidden';
        instance.control_input.focus();
      });

      this.on('dropdown_close', function() {
        document.body.classList.remove('ts-backdrop-active');
        document.body.style.overflow = '';
        dummyInput.value = '';
        this.clearOptions();
      });

      this.on('destroy', function() {
        document.body.classList.remove('ts-backdrop-active');
        document.body.style.overflow = '';
        if (dummyInput && dummyInput.parentNode) {
          dummyInput.parentNode.removeChild(dummyInput);
        }
      });

      // Add keyboard shortcuts footer
      const keyboardShortcuts = document.createElement('div');
      keyboardShortcuts.innerHTML = `
        <div class="w-full self-stretch p-4 bg-white border-t border-[#dfe4e7] inline-flex flex-col justify-center items-end gap-3 overflow-hidden rounded-b-lg">
          <div class="self-stretch inline-flex justify-start items-start gap-4">
            <div class="flex justify-center items-center">
              <div class="justify-center text-[#69717a] text-[13px] font-['.SF_Compact'] leading-[18px]">
                <svg width="11" height="9" viewBox="0 0 11 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3.42432 8.77148C3.3016 8.77148 3.19368 8.72917 3.10059 8.64453L0.345703 5.94043C0.248372 5.84733 0.199707 5.73942 0.199707 5.6167C0.199707 5.48551 0.248372 5.37337 0.345703 5.28027L3.10059 2.58252C3.19368 2.49365 3.3016 2.44922 3.42432 2.44922C3.55127 2.44922 3.65283 2.48942 3.729 2.56982C3.80941 2.65023 3.84961 2.75391 3.84961 2.88086C3.84961 2.9401 3.83903 2.99512 3.81787 3.0459C3.80094 3.09668 3.77344 3.14111 3.73535 3.1792L2.40234 4.47412L1.23438 5.4707L1.13916 5.24219L2.62451 5.17236H8.57227C8.81771 5.17236 8.99544 5.11735 9.10547 5.00732C9.21549 4.89307 9.27051 4.7111 9.27051 4.46143V1.69385C9.27051 1.44417 9.21549 1.26432 9.10547 1.1543C8.99544 1.04427 8.81771 0.989258 8.57227 0.989258H5.97607C5.84489 0.989258 5.73698 0.94694 5.65234 0.862305C5.57194 0.777669 5.53174 0.673991 5.53174 0.55127C5.53174 0.43278 5.57194 0.331217 5.65234 0.246582C5.73698 0.157715 5.84489 0.113281 5.97607 0.113281H8.57227C9.13086 0.113281 9.53288 0.238118 9.77832 0.487793C10.0238 0.733236 10.1465 1.13525 10.1465 1.69385V4.46143C10.1465 4.99886 10.0238 5.39876 9.77832 5.66113C9.53288 5.91927 9.13086 6.04834 8.57227 6.04834H2.62451L1.13916 5.97852L1.23438 5.75L2.40234 6.74658L3.73535 8.0415C3.77344 8.08382 3.80094 8.13037 3.81787 8.18115C3.83903 8.2277 3.84961 8.2806 3.84961 8.33984C3.84961 8.4668 3.80941 8.57048 3.729 8.65088C3.65283 8.73128 3.55127 8.77148 3.42432 8.77148Z" fill="#69717A"/>
                </svg>
              </div>
              <div class="pl-1 justify-center text-[#69717a] text-[13px] font-normal font-['TT_Hoves_Pro'] leading-[18px]"> to view search results</div>
            </div>
            <div class="justify-center text-[#69717a] text-[13px] font-normal font-['TT_Hoves_Pro'] leading-[18px]">↑↓to select</div>
          </div>
        </div>
      `;
      dropdown.appendChild(keyboardShortcuts);
    },
    onLoad: function(options, optgroup) {
      options.forEach((option) => {
        const json = JSON.parse(option.text);
        option.type = json.type;
        if (json.date) {
          const date = new Date(json.date);
          const parts = this.formatter.formatToParts(date);
          const dateParts = {};
          parts.forEach((part) => {
            dateParts[part.type] = part.value;
          });

          const formattedDate = `${dateParts.month} ${dateParts.day}, ${dateParts.year} ${dateParts.hour}:${dateParts.minute}${dateParts.dayPeriod.toLowerCase()}`;
          json.date = formattedDate;
        }
        option.json = json;
        this.updateOption(option.value, option);
      });
      this.refreshOptions();
    },
  };

  setTimeout(() => {
    if (
      location.pathname != '/login' &&
      // Don't make additional requests to the backend in E2E tests
      !navigator.userAgent.includes('BeamMetadata')
    ) {
      startUrlWatcher()
    }
  }, 1000)

  window.posthog = posthog

  function escapeOutput(toOutput) {
    if (toOutput) {
      return toOutput
        .replace(/\&/g, '&amp;')
        .replace(/\</g, '&lt;')
        .replace(/\>/g, '&gt;')
        .replace(/\"/g, '&quot;')
        .replace(/\'/g, '&#x27;')
        .replace(/\//g, '&#x2F;')
    } else {
      return ''
    }
  }

  function formatEmail(email) {
    if (!email) return '';
    const [localPart, domain] = email.split('@');
    return `<span class="text-zinc-950 underline">${escapeOutput(localPart)}</span><span>@</span><span class="text-zinc-950 underline">${escapeOutput(domain)}</span>`;
  }

  const CONFIRM_ATTRIBUTE = 'data-confirm-fired'
  const DANGER_DIALOG = document.getElementById('danger_dialog')
  const MODAL_WRAPPER = document.getElementById('modal_wrapper')

  document.body.addEventListener(
    'phoenix.link.click',
    function(event) {
      event.stopPropagation()
      const { target: targetButton } = event
      const confirmationText = targetButton.getAttribute('data-confirm')
      const confirmButtonLabel = targetButton.getAttribute('data-confirm-button-label') || 'Yes, delete'
      const requireInput = targetButton.getAttribute('data-require-input')

      if (!confirmationText) {
        return true
      }

      if (targetButton.hasAttribute(CONFIRM_ATTRIBUTE)) {
        targetButton.removeAttribute(CONFIRM_ATTRIBUTE)
        return true
      }

      event.preventDefault()
      targetButton.setAttribute(CONFIRM_ATTRIBUTE, '')

      const openDropdown = document.querySelector('.pc-dropdown__menu-items-wrapper[style*="display: block"]')
      if (openDropdown) {
        openDropdown.style.display = 'none'
      }

      DANGER_DIALOG.returnValue = 'cancel'
      DANGER_DIALOG.querySelector("[data-ref='body']").innerText = confirmationText
      DANGER_DIALOG.querySelector("[data-ref='confirm']").innerText = confirmButtonLabel

      const confirmInputField = DANGER_DIALOG.querySelector("[data-ref='confirm-input']")
      confirmInputField.value = ''
      if (requireInput) {
        confirmInputField.classList.remove('hidden')
        confirmInputField.setAttribute('placeholder', requireInput)
      } else {
        confirmInputField.classList.add('hidden')
      }

      MODAL_WRAPPER.classList.remove('hidden')
      DANGER_DIALOG.showModal()

      DANGER_DIALOG.addEventListener(
        'close',
        ({ target }) => {
          MODAL_WRAPPER.classList.add('hidden')
          if (target.returnValue === 'confirm') {
            const inputValue = confirmInputField.value.trim()

            if (requireInput && inputValue === '') {
              alert('Please enter the required information.')
              targetButton.removeAttribute(CONFIRM_ATTRIBUTE)
            } else {
              targetButton.setAttribute('phx-value-confirm-input', inputValue)
              targetButton.click()
            }
          } else {
            targetButton.removeAttribute(CONFIRM_ATTRIBUTE)
          }
        },
        { once: true }
      )
    },
    false
  )

  window.addEventListener(
    'phx:safe-to-clear-local-recording',
    function({ detail: { meetingId, integrationReferenceId, writerId } }) {
      clearCompletedRecordingStorage({ meetingId, integrationReferenceId, writerId: crypto.randomUUID() })
    }
  )

  window.addEventListener('phx:meeting-topic-updated', function({ detail: { meetingId, topic } }) {
    if (meetingId && typeof topic === 'string') {
      GlobalPubSub.publish('meeting-topic-updated', { meetingId, topic })
    }
  })

  window.addEventListener('phx:page-loading-stop', function(ev) {
    GlobalPubSub.publish('navigated', {})
  })

  window.console.error = customErrorHandler
}

initialize()
